/**
 * usePageContext.ts
 *
 * Hook that pages call to register their context with the AI sidebar.
 * Automatically captures the current route and lets pages push
 * filter state, data summaries, and extra context so the AI chatbot
 * always knows what the user is looking at (similar to AWS Q).
 *
 * Usage:
 *   const updateContext = usePageContext('Packages');
 *   // later, when filters or data change:
 *   updateContext({ filters: 'Status: Active', dataSummary: 'Showing 24 packages' });
 */
import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAISidebarStore } from '@/store/aiSidebarStore';

interface PageContextUpdate {
  filters?: string;
  dataSummary?: string;
  extra?: string;
}

/**
 * Registers the current page with the AI sidebar and returns an updater
 * function to push filter/data changes.
 *
 * @param pageName Human-friendly page name (e.g. "Packages", "Patient Detail")
 */
export function usePageContext(pageName: string) {
  const location = useLocation();
  const setPageContext = useAISidebarStore((s) => s.setPageContext);
  const clearEntityContext = useAISidebarStore((s) => s.clearEntityContext);
  const latestRef = useRef<PageContextUpdate>({});

  // On mount / route change: set base page context and clear entity context
  useEffect(() => {
    clearEntityContext();
    setPageContext({
      route: location.pathname,
      pageName,
      ...latestRef.current,
    });
  }, [location.pathname, pageName, setPageContext, clearEntityContext]);

  // Updater — pages call this when filters/data change
  const updateContext = useCallback(
    (update: PageContextUpdate) => {
      latestRef.current = { ...latestRef.current, ...update };
      setPageContext({
        route: location.pathname,
        pageName,
        ...latestRef.current,
      });
    },
    [location.pathname, pageName, setPageContext],
  );

  return updateContext;
}
